<script lang="ts">
  import RecipientPicker, { type RecipientPerson } from './RecipientPicker.svelte';
  import { onMount, untrack } from 'svelte';
  import { npubEncode } from 'nostr-tools/nip19';
  import type { BadgeDefinition, BitcoinPlusPlusGrant, BitcoinPlusPlusOrganization, PublicationResult } from './api';
  import { submitOrganizationEvent } from './api';
  import { parseRecipients, signBadgeAwards, type RecipientRow } from './badgeEvents';
  import type { SignerConnection } from './signers';

  let { badges, signer, relays, onissued, directoryOrganization = null, canBrowseEcosystem = false, bitcoinPlusPlusURL = '', initialGrantID = '' }: {
    badges: BadgeDefinition[];
    signer: SignerConnection;
    relays: string[];
    onissued: () => void;
    directoryOrganization?: BitcoinPlusPlusOrganization | null;
    canBrowseEcosystem?: boolean;
    bitcoinPlusPlusURL?: string;
    initialGrantID?: string;
  } = $props();

  let selectedID = $state(untrack(() => badges[0]?.identifier ?? ''));
  let source = $state('');
  let manualOpen = $state(untrack(() => !directoryOrganization));
  let rows = $derived(parseRecipients(source));
  let validRows = $derived(rows.filter((row) => row.pubkey && !row.error && !row.duplicate));
  let invalidRows = $derived(rows.filter((row) => row.error || row.duplicate));
  let phase = $state<'edit' | 'sign' | 'publish' | 'done'>('edit');
  let completed = $state<PublicationResult[]>([]);
  let error = $state('');
  let directoryPersonIDs = $state<Record<string, string>>({});
  let directoryGrantIDs = $state<Record<string, string>>({});
	let pendingFingerprint = $state('');
	let pendingEvents = $state<Awaited<ReturnType<typeof signBadgeAwards>>>([]);
	let pendingIdempotencyKeys = $state<string[]>([]);
  let readyGrants = $derived((directoryOrganization?.badge_grants ?? []).filter((grant) => grant.state === 'ready_to_issue' && grant.badge_identifier === selectedID && grant.recipient_pubkey));

  onMount(() => {
    const grant = (directoryOrganization?.badge_grants ?? []).find((item) => item.id === initialGrantID && item.state === 'ready_to_issue' && item.recipient_pubkey);
    if (!grant) return;
    selectedID = grant.badge_identifier;
    addReadyGrant(grant);
  });

  function addDirectoryPerson(person: RecipientPerson) {
    if (!person.nostr_pubkey) return;
    if (validRows.some((row) => row.pubkey === person.nostr_pubkey)) return;
		directoryPersonIDs = { ...directoryPersonIDs, [person.nostr_pubkey]: person.id };
    const escapedName = `"${person.name.replaceAll('"', '""')}"`;
    const row = [escapedName, npubEncode(person.nostr_pubkey), person.profile_url ?? ''].join(',');
    source = source.trim() ? `${source.trim()}\n${row}` : row;
  }

  const selectedPeople = $derived(validRows.map(row => {
    const member = directoryOrganization?.members?.find(person => person.nostr_pubkey === row.pubkey);
    return { id: member?.id ?? directoryPersonIDs[row.pubkey!] ?? row.pubkey!, name: row.label || 'Nostr recipient', nostr_pubkey: row.pubkey!, profile_url: row.subjectProfileURL };
  }));

  function removePerson(pubkey: string) {
    source = rows.filter(row => row.pubkey !== pubkey).map(row => row.source).join('\n');
    delete directoryPersonIDs[pubkey];
    delete directoryGrantIDs[pubkey];
  }

  function addReadyGrant(grant: BitcoinPlusPlusGrant) {
    if (!grant.recipient_pubkey || validRows.some((row) => row.pubkey === grant.recipient_pubkey)) return;
    directoryPersonIDs = { ...directoryPersonIDs, [grant.recipient_pubkey]: grant.recipient_person_id };
    directoryGrantIDs = { ...directoryGrantIDs, [grant.recipient_pubkey]: grant.id };
    const escapedName = `"${grant.recipient_name.replaceAll('"', '""')}"`;
		const row = [escapedName, npubEncode(grant.recipient_pubkey), grant.subject_profile_url ?? ''].join(',');
    source = source.trim() ? `${source.trim()}\n${row}` : row;
  }

  async function importCSV(event: Event) {
    const file = (event.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    source = await file.text();
    manualOpen = true;
  }

  async function issue() {
    const badge = badges.find((item) => item.identifier === selectedID);
    if (!badge || validRows.length === 0) return;
    error = '';
    completed = [];
    try {
      phase = 'sign';
		const batchContext = {
			organization_id: directoryOrganization?.id ?? '',
			recipients: validRows.map((row) => {
				const pubkey = row.pubkey!;
				const member = directoryOrganization?.members?.find((item) => item.nostr_pubkey === pubkey);
				return {
					pubkey,
					person_id: member?.id ?? directoryPersonIDs[pubkey] ?? '',
					grant_id: directoryGrantIDs[pubkey] ?? '',
					subject_profile_url: row.subjectProfileURL
				};
			})
		};
		const fingerprint = JSON.stringify({ issuer: signer.pubkey, badge: badge.identifier, relay: relays[0] ?? '', ...batchContext });
		if (pendingFingerprint !== fingerprint || pendingEvents.length !== validRows.length || pendingIdempotencyKeys.length !== validRows.length) {
			pendingEvents = await signBadgeAwards(signer, badge, validRows.map((row) => row.pubkey!), relays[0] ?? '', batchContext);
			pendingIdempotencyKeys = pendingEvents.map(() => crypto.randomUUID());
			pendingFingerprint = fingerprint;
		}
		const events = pendingEvents;
      phase = 'publish';
		for (const [index, event] of events.entries()) {
			const row = validRows[index];
			const profiles = row?.subjectProfileURL && row.pubkey ? { [row.pubkey]: row.subjectProfileURL } : {};
			const member = directoryOrganization?.members?.find((item) => item.nostr_pubkey === row?.pubkey);
			const personID = row?.pubkey ? (member?.id ?? directoryPersonIDs[row.pubkey]) : '';
			const personIDs = personID && row?.pubkey ? { [row.pubkey]: personID } : {};
			const grantIDs = row?.pubkey && directoryGrantIDs[row.pubkey] ? { [row.pubkey]: directoryGrantIDs[row.pubkey] } : {};
			completed = [...completed, await submitOrganizationEvent(event, profiles, personIDs, directoryOrganization?.id ?? '', grantIDs, pendingIdempotencyKeys[index])];
		}
		pendingFingerprint = '';
		pendingEvents = [];
		pendingIdempotencyKeys = [];
      phase = 'done';
      onissued();
    } catch (cause) {
      phase = 'edit';
      error = cause instanceof Error ? cause.message : typeof cause === 'string' && cause.trim() ? cause : 'The awards could not be issued.';
    }
  }

  function exportRejected() {
    const escaped = (value: string) => `"${value.replaceAll('"', '""')}"`;
    const csv = ['input,label,problem', ...invalidRows.map((row) => [row.source, row.label, row.duplicate ? 'duplicate' : row.error ?? ''].map(escaped).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'badge-recipients-rejected.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function exportResults() {
    const escaped = (value: string) => `"${value.replaceAll('"', '""')}"`;
	const csv = ['npub,label,subject_profile_url,event_id,published,accepted_relays', ...validRows.map((row, index) => {
	  const publication = completed[index];
      const accepted = publication?.publications.filter((item) => item.accepted).map((item) => item.relay_url).join(' ') ?? '';
		return [row.pubkey ? npubEncode(row.pubkey) : '', row.label, row.subjectProfileURL, publication?.event_id ?? '', String(Boolean(publication?.published)), accepted].map(escaped).join(',');
    })].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'badge-issuance-results.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function shortKey(row: RecipientRow): string {
    if (!row.pubkey) return row.source;
    const npub = npubEncode(row.pubkey);
    return `${npub.slice(0, 13)}…${npub.slice(-7)}`;
  }
</script>

{#if badges.length === 0}
  <div class="needs-badge">
    <span>01</span>
    <div><p class="eyebrow">before issuing</p><h3>Create a badge first.</h3><p>Your catalog needs at least one signed definition before it can be awarded.</p></div>
  </div>
{:else if phase === 'done'}
  <div class="issued">
    <svg viewBox="0 0 120 120" aria-hidden="true"><circle cx="60" cy="60" r="48"/><path d="M35 61l17 17 34-38"/></svg>
    <p class="eyebrow">award complete</p>
    <h3>{validRows.length} {validRows.length === 1 ? 'person received' : 'people received'} this badge.</h3>
    {#if completed.length}<p>{completed.filter((item) => item.published).length} of {completed.length} award {completed.length === 1 ? 'event has' : 'events have'} reached at least one relay.</p>{/if}
    <div class="success-actions">{#if completed.length}<button class="secondary" type="button" onclick={exportResults}>Export results ↓</button>{/if}<button class="primary" type="button" onclick={() => { source = ''; directoryGrantIDs = {}; phase = 'edit'; }}>Issue another batch <span>→</span></button></div>
  </div>
{:else}
  <fieldset class="award-grid" disabled={phase !== 'edit'}>
    <section>
      <p class="eyebrow">01 · choose the recognition</p>
      <div class="badge-choices">
        {#each badges as badge}
          <label class:selected={selectedID === badge.identifier}>
            <input type="radio" name="badge" value={badge.identifier} bind:group={selectedID} />
            <img src={badge.thumbnails.find((thumb) => thumb.width === 64)?.url ?? badge.image_url} alt="" />
            <span><strong>{badge.name}</strong><small>#{badge.identifier}</small></span>
            <i aria-hidden="true"></i>
          </label>
        {/each}
      </div>
    </section>

    <section>
      <div class="recipient-heading">
        <div><p class="eyebrow">02 · add recipients</p><h3>Choose who receives this badge.</h3></div>
        <label class="csv-button">Import CSV<input type="file" accept=".csv,text/csv,text/plain" onchange={importCSV} /></label>
      </div>

    <RecipientPicker organization={directoryOrganization} selected={selectedPeople} {canBrowseEcosystem} {bitcoinPlusPlusURL} onadd={addDirectoryPerson} onremove={removePerson} />
		{#if readyGrants.length}
			<div class="directory-picker ready-grants"><div><span><strong>ready Bitcoin++ grants</strong><small>verified since the grant was created</small></span></div><div class="directory-members">{#each readyGrants as grant}<button type="button" onclick={() => addReadyGrant(grant)}><span>→</span>{grant.recipient_name}</button>{/each}</div></div>
		{/if}
      <details class="manual-entry" bind:open={manualOpen}><summary>Paste or edit recipient keys</summary>
		<textarea id="recipient-keys" bind:value={source} rows="10" placeholder={'npub1…\nAlex, npub1…, https://example.org/people/alex\n<64-character hex key>'} aria-label="Badge recipients"></textarea>
			<p class="input-note">Paste npubs or hex keys. CSV rows can be <code>name, npub, subject_profile_url</code> or <code>npub, name, subject_profile_url</code>. Profile URLs are optional presentation links; Bitcoin++ links follow the recipient's current public profile settings.</p></details>

      <div class="tally" aria-live="polite">
        <div><strong>{validRows.length}</strong><span>ready</span></div>
        <div class:has-errors={invalidRows.length > 0}><strong>{invalidRows.length}</strong><span>need attention</span></div>
        <div><strong>{validRows.length}</strong><span>signed events</span></div>
      </div>

      {#if rows.length > 0}
        <div class="review">
          {#each rows.slice(0, 6) as row}
            <div class:invalid={row.error || row.duplicate}>
              <span class="state">{row.error || row.duplicate ? '×' : '✓'}</span>
								<span><strong>{row.label || 'Nostr recipient'}</strong><small>{shortKey(row)}</small>{#if row.subjectProfileURL}<small class="profile-url">{row.subjectProfileURL}</small>{/if}</span>
              {#if row.error || row.duplicate}<em>{row.duplicate ? 'Duplicate' : row.error}</em>{/if}
            </div>
          {/each}
          {#if rows.length > 6}<p>+ {rows.length - 6} more rows</p>{/if}
        </div>
      {/if}

      {#if invalidRows.length > 0}<button class="export" type="button" onclick={exportRejected}>Export {invalidRows.length} rejected {invalidRows.length === 1 ? 'row' : 'rows'} ↓</button>{/if}
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      <button class="primary issue" type="button" onclick={issue} disabled={phase !== 'edit' || validRows.length === 0}>
		{phase === 'sign' ? 'Check your signer…' : phase === 'publish' ? `Publishing ${completed.length + 1}…` : `Review & issue ${validRows.length || ''}`}
        <span>{phase === 'edit' ? '→' : '···'}</span>
      </button>
    </section>
  </fieldset>
{/if}

<style>
  .eyebrow { margin: 0 0 13px; color: var(--violet); font-size: 10px; font-weight: 850; letter-spacing: .14em; }
  .award-grid { border: 0; padding: 0; margin: 45px 0 0; min-width: 0; display: grid; grid-template-columns: minmax(250px, .7fr) minmax(400px, 1.3fr); gap: clamp(35px, 7vw, 90px); margin-top: 45px; }
  .badge-choices { display: grid; border-top: 1px solid var(--line); }
  .badge-choices label { display: grid; grid-template-columns: 52px 1fr 18px; align-items: center; gap: 13px; min-height: 76px; padding: 11px 4px; border-bottom: 1px solid var(--line); cursor: pointer; }
  .badge-choices label.selected { background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--aqua) 9%, transparent)); }
  .badge-choices label:has(input:focus-visible), .csv-button:focus-within { outline: 3px solid color-mix(in srgb, var(--aqua) 42%, transparent); outline-offset: 3px; }
  .badge-choices input { position: absolute; opacity: 0; pointer-events: none; }
  .badge-choices img { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; }
  .badge-choices span { display: grid; min-width: 0; gap: 4px; }
  .badge-choices strong { overflow: hidden; font-family: var(--display); font-size: 17px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
  .badge-choices small { color: var(--muted); font: 11px var(--mono); }
  .badge-choices i { width: 15px; height: 15px; border: 1px solid var(--line-strong); border-radius: 50%; box-shadow: inset 0 0 0 3px var(--surface); }
  .badge-choices label.selected i { background: var(--violet); }
  .recipient-heading { display: flex; align-items: end; justify-content: space-between; gap: 20px; }
  h3 { margin: 0; font-family: var(--display); font-size: 31px; font-weight: 500; letter-spacing: -.03em; }
  .csv-button { min-height: 42px; display: grid; place-items: center; padding: 8px 14px; border: 1px solid var(--line-strong); border-radius: 999px; font-size: 12px; font-weight: 780; cursor: pointer; }
  .csv-button input { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
  textarea { width: 100%; min-height: 230px; margin-top: 19px; resize: vertical; padding: 16px; border: 1px solid var(--line-strong); border-radius: 0; background: var(--surface-soft); color: var(--ink); font: 13px/1.6 var(--mono); }
  .directory-picker { margin-top: 12px; border: 1px solid var(--line); background: color-mix(in srgb, var(--aqua) 7%, var(--surface)); }
  .directory-picker > div:first-child { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 12px; border-bottom: 1px solid var(--line); }
  .directory-picker span { display: grid; gap: 2px; }
  .directory-picker small { color: var(--muted); font-size: 10px; }
  .directory-picker button { display: inline-flex; align-items: center; min-height: 38px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--ink); padding: 7px 10px; font-size: 11px; font-weight: 780; cursor: pointer; text-decoration: none; }
  .directory-members { display: flex; flex-wrap: wrap; gap: 7px; max-height: 160px; overflow: auto; padding: 10px; }
  .directory-members button span { display: inline; margin-right: 5px; color: var(--teal); }
  .manual-entry { margin-top: 20px; }
  summary { cursor: pointer; font-size: 13px; font-weight: 700; padding: 12px 0; }
  .tally { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 22px; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
  .tally div { display: grid; gap: 2px; padding: 14px 10px; border-right: 1px solid var(--line); }
  .tally div:last-child { border: 0; }
  .tally strong { font: 26px var(--display); }
  .tally span { color: var(--muted); font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  .tally .has-errors strong { color: var(--coral-dark); }
  .review { margin-top: 15px; }
  .review > div { display: grid; grid-template-columns: 23px 1fr auto; align-items: center; gap: 9px; padding: 8px 3px; border-bottom: 1px solid var(--line); }
  .review .state { display: grid; place-items: center; width: 19px; height: 19px; border-radius: 50%; background: var(--teal); color: white; font-size: 10px; }
  .review > div.invalid .state { background: var(--coral); }
  .review > div > span:nth-child(2) { display: grid; min-width: 0; }
  .review strong { font-size: 13px; }
  .review small { overflow: hidden; color: var(--muted); font: 11px var(--mono); text-overflow: ellipsis; white-space: nowrap; }
	.review .profile-url { color: var(--teal); }
  .review em { max-width: 170px; color: var(--coral-dark); font-size: 11px; font-style: normal; text-align: right; }
  .review > p { color: var(--muted); font-size: 12px; }
  .export { margin-top: 12px; border: 0; background: transparent; color: var(--coral-dark); font-size: 12px; font-weight: 750; cursor: pointer; }
  .primary { min-height: 47px; display: inline-flex; align-items: center; justify-content: space-between; gap: 25px; padding: 10px 13px 10px 19px; border: 0; border-radius: 999px; background: var(--ink); color: white; font-weight: 780; cursor: pointer; }
  .primary span { color: var(--gold); }
  .primary.issue { width: 100%; margin-top: 24px; }
  .error { padding: 10px 13px; border-left: 3px solid var(--coral); background: #f9e4e4; color: #842f38; font-size: 13px; }
  .needs-badge, .issued { display: grid; justify-items: center; max-width: 650px; margin: 48px auto 0; padding: 48px; border: 1px solid var(--line); text-align: center; }
  .needs-badge > span { color: var(--aqua); font: italic 68px var(--display); }
  .needs-badge p:last-child, .issued > p:not(.eyebrow) { color: var(--muted); line-height: 1.55; }
  .issued svg { width: 100px; fill: color-mix(in srgb, var(--teal) 12%, transparent); stroke: var(--teal); stroke-width: 5; stroke-linecap: round; stroke-linejoin: round; }
  .issued h3 { max-width: 560px; font-size: clamp(34px, 5vw, 52px); }
  .issued .primary { margin-top: 13px; }
  .success-actions { display: flex; justify-content: center; flex-wrap: wrap; gap: 10px; margin-top: 13px; }
  .success-actions .primary { margin-top: 0; }
  .secondary { min-height: 47px; padding: 10px 17px; border: 1px solid var(--line-strong); border-radius: 999px; background: transparent; color: var(--ink); font-weight: 780; cursor: pointer; }
  @media (max-width: 760px) { .award-grid { grid-template-columns: 1fr; } }
  @media (max-width: 480px) { .recipient-heading { align-items: flex-start; flex-direction: column; } .csv-button { width: 100%; } .tally span { font-size: 9px; } .review > div { grid-template-columns: 23px 1fr; } .review em { grid-column: 2; max-width: none; text-align: left; } .needs-badge, .issued { padding: 35px 18px; } }
</style>
